/**
 * MEU ERP API - Admin Equipment Service
 * Admin endpoints for viewing user equipment and order history
 */
/* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpEvent,
} from '@angular/common/http';
import { CustomHttpUrlEncodingCodec } from '../encoder';

import { Observable } from 'rxjs';

import { BASE_PATH, COLLECTION_FORMATS } from '../variables';
import { Configuration } from '../configuration';

@Injectable()
export class AdminEquipmentService {
  protected basePath = '/';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration
  ) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  /**
   * GET danh sách thiết bị của user
   * GET /api/admin/users/{userId}/my-equipment
   */
  public apiAdminUsersEquipmentGet(
    userId: string,
    filters?: string,
    sorts?: string,
    page?: number,
    pageSize?: number,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<any>;
  public apiAdminUsersEquipmentGet(
    userId: string,
    filters?: string,
    sorts?: string,
    page?: number,
    pageSize?: number,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<any>>;
  public apiAdminUsersEquipmentGet(
    userId: string,
    filters?: string,
    sorts?: string,
    page?: number,
    pageSize?: number,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<any>>;
  public apiAdminUsersEquipmentGet(
    userId: string,
    filters?: string,
    sorts?: string,
    page?: number,
    pageSize?: number,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    if (userId === null || userId === undefined) {
      throw new Error(
        'Required parameter userId was null or undefined when calling apiAdminUsersEquipmentGet.'
      );
    }

    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (filters !== undefined && filters !== null) {
      queryParameters = queryParameters.set('Filters', filters);
    }
    if (sorts !== undefined && sorts !== null) {
      queryParameters = queryParameters.set('Sorts', sorts);
    }
    if (page !== undefined && page !== null) {
      queryParameters = queryParameters.set('Page', page.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      queryParameters = queryParameters.set('PageSize', pageSize.toString());
    }

    let headers = this.defaultHeaders;

    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.basePath}/api/admin/users/${encodeURIComponent(
        String(userId)
      )}/my-equipment`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * GET chi tiết thiết bị của user
   * GET /api/admin/users/{userId}/my-equipment/{equipmentId}
   */
  public apiAdminUsersEquipmentIdGet(
    userId: string,
    equipmentId: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<any>;
  public apiAdminUsersEquipmentIdGet(
    userId: string,
    equipmentId: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<any>>;
  public apiAdminUsersEquipmentIdGet(
    userId: string,
    equipmentId: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<any>>;
  public apiAdminUsersEquipmentIdGet(
    userId: string,
    equipmentId: string,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    if (userId === null || userId === undefined) {
      throw new Error(
        'Required parameter userId was null or undefined when calling apiAdminUsersEquipmentIdGet.'
      );
    }
    if (equipmentId === null || equipmentId === undefined) {
      throw new Error(
        'Required parameter equipmentId was null or undefined when calling apiAdminUsersEquipmentIdGet.'
      );
    }

    let headers = this.defaultHeaders;

    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.basePath}/api/admin/users/${encodeURIComponent(
        String(userId)
      )}/my-equipment/${encodeURIComponent(String(equipmentId))}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * GET lịch sử đơn hàng theo thiết bị của user
   * GET /api/admin/users/{userId}/my-equipment/{equipmentId}/orders
   */
  public apiAdminUsersEquipmentOrdersGet(
    userId: string,
    equipmentId: string,
    page?: number,
    pageSize?: number,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<any>;
  public apiAdminUsersEquipmentOrdersGet(
    userId: string,
    equipmentId: string,
    page?: number,
    pageSize?: number,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<any>>;
  public apiAdminUsersEquipmentOrdersGet(
    userId: string,
    equipmentId: string,
    page?: number,
    pageSize?: number,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<any>>;
  public apiAdminUsersEquipmentOrdersGet(
    userId: string,
    equipmentId: string,
    page?: number,
    pageSize?: number,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    if (userId === null || userId === undefined) {
      throw new Error(
        'Required parameter userId was null or undefined when calling apiAdminUsersEquipmentOrdersGet.'
      );
    }
    if (equipmentId === null || equipmentId === undefined) {
      throw new Error(
        'Required parameter equipmentId was null or undefined when calling apiAdminUsersEquipmentOrdersGet.'
      );
    }

    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (page !== undefined && page !== null) {
      queryParameters = queryParameters.set('Page', page.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      queryParameters = queryParameters.set('PageSize', pageSize.toString());
    }

    let headers = this.defaultHeaders;

    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.basePath}/api/admin/users/${encodeURIComponent(
        String(userId)
      )}/my-equipment/${encodeURIComponent(String(equipmentId))}/orders`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * GET danh sách thiết bị của tất cả user
   * GET /api/admin/my-equipment
   */
  public apiAdminMyEquipmentGet(
    filters?: string,
    sorts?: string,
    page?: number,
    pageSize?: number,
    name?: string,
    startDate?: string,
    endDate?: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<any>;
  public apiAdminMyEquipmentGet(
    filters?: string,
    sorts?: string,
    page?: number,
    pageSize?: number,
    name?: string,
    startDate?: string,
    endDate?: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<any>>;
  public apiAdminMyEquipmentGet(
    filters?: string,
    sorts?: string,
    page?: number,
    pageSize?: number,
    name?: string,
    startDate?: string,
    endDate?: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<any>>;
  public apiAdminMyEquipmentGet(
    filters?: string,
    sorts?: string,
    page?: number,
    pageSize?: number,
    name?: string,
    startDate?: string,
    endDate?: string,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (filters !== undefined && filters !== null) {
      queryParameters = queryParameters.set('Filters', filters);
    }
    if (sorts !== undefined && sorts !== null) {
      queryParameters = queryParameters.set('Sorts', sorts);
    }
    if (page !== undefined && page !== null) {
      queryParameters = queryParameters.set('Page', page.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      queryParameters = queryParameters.set('PageSize', pageSize.toString());
    }
    if (name !== undefined && name !== null) {
      queryParameters = queryParameters.set('name', name);
    }
    if (startDate !== undefined && startDate !== null) {
      queryParameters = queryParameters.set('startDate', startDate);
    }
    if (endDate !== undefined && endDate !== null) {
      queryParameters = queryParameters.set('endDate', endDate);
    }

    let headers = this.defaultHeaders;

    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.basePath}/api/admin/my-equipment`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * GET thống kê thiết bị (Admin Dashboard)
   * GET /api/admin/my-equipment/statistics
   */
  public apiAdminMyEquipmentStatisticsGet(
    userId?: string,
    brandId?: string,
    equipmentModelId?: string,
    status?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<any>;
  public apiAdminMyEquipmentStatisticsGet(
    userId?: string,
    brandId?: string,
    equipmentModelId?: string,
    status?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<any>>;
  public apiAdminMyEquipmentStatisticsGet(
    userId?: string,
    brandId?: string,
    equipmentModelId?: string,
    status?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<any>>;
  public apiAdminMyEquipmentStatisticsGet(
    userId?: string,
    brandId?: string,
    equipmentModelId?: string,
    status?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (userId !== undefined && userId !== null) {
      queryParameters = queryParameters.set('userId', userId);
    }
    if (brandId !== undefined && brandId !== null) {
      queryParameters = queryParameters.set('brandId', brandId);
    }
    if (equipmentModelId !== undefined && equipmentModelId !== null) {
      queryParameters = queryParameters.set('equipmentModelId', equipmentModelId);
    }
    if (status !== undefined && status !== null) {
      queryParameters = queryParameters.set('status', status);
    }
    if (fromDate !== undefined && fromDate !== null) {
      queryParameters = queryParameters.set('fromDate', fromDate);
    }
    if (toDate !== undefined && toDate !== null) {
      queryParameters = queryParameters.set('toDate', toDate);
    }
    if (topLimit !== undefined && topLimit !== null) {
      queryParameters = queryParameters.set('topLimit', topLimit.toString());
    }

    let headers = this.defaultHeaders;

    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.basePath}/api/admin/my-equipment/statistics`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * GET top sản phẩm mua nhiều nhất theo thiết bị / khách hàng
   * GET /api/admin/my-equipment/top-purchased-products
   */
  public apiAdminMyEquipmentTopPurchasedProductsGet(
    userId?: string,
    myEquipmentId?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<any>;
  public apiAdminMyEquipmentTopPurchasedProductsGet(
    userId?: string,
    myEquipmentId?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<any>>;
  public apiAdminMyEquipmentTopPurchasedProductsGet(
    userId?: string,
    myEquipmentId?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<any>>;
  public apiAdminMyEquipmentTopPurchasedProductsGet(
    userId?: string,
    myEquipmentId?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (userId !== undefined && userId !== null) {
      queryParameters = queryParameters.set('userId', userId);
    }
    if (myEquipmentId !== undefined && myEquipmentId !== null) {
      queryParameters = queryParameters.set('myEquipmentId', myEquipmentId);
    }
    if (fromDate !== undefined && fromDate !== null) {
      queryParameters = queryParameters.set('fromDate', fromDate);
    }
    if (toDate !== undefined && toDate !== null) {
      queryParameters = queryParameters.set('toDate', toDate);
    }
    if (topLimit !== undefined && topLimit !== null) {
      queryParameters = queryParameters.set('topLimit', topLimit.toString());
    }

    let headers = this.defaultHeaders;

    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.basePath}/api/admin/my-equipment/top-purchased-products`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * PUT cập nhật thiết bị của user bởi admin
   * PUT /api/admin/users/{userId}/my-equipment/{equipmentId}
   */
  public apiAdminUsersEquipmentIdPut(
    userId: string,
    equipmentId: string,
    body: any,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<any>;
  public apiAdminUsersEquipmentIdPut(
    userId: string,
    equipmentId: string,
    body: any,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<any>>;
  public apiAdminUsersEquipmentIdPut(
    userId: string,
    equipmentId: string,
    body: any,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<any>>;
  public apiAdminUsersEquipmentIdPut(
    userId: string,
    equipmentId: string,
    body: any,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    if (userId === null || userId === undefined) {
      throw new Error(
        'Required parameter userId was null or undefined when calling apiAdminUsersEquipmentIdPut.'
      );
    }
    if (equipmentId === null || equipmentId === undefined) {
      throw new Error(
        'Required parameter equipmentId was null or undefined when calling apiAdminUsersEquipmentIdPut.'
      );
    }

    let headers = this.defaultHeaders;
    headers = headers.set('Content-Type', 'application/json');

    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'put',
      `${this.basePath}/api/admin/users/${encodeURIComponent(
        String(userId)
      )}/my-equipment/${encodeURIComponent(String(equipmentId))}`,
      {
        body: body,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * GET repair cost report — ranks equipment by total repair cost (across all customers)
   * GET /api/admin/my-equipment/repairCostReport
   */
  public apiAdminMyEquipmentRepairCostReportGet(
    userId?: string,
    brandId?: string,
    equipmentModelId?: string,
    fromDate?: string,
    toDate?: string,
    topLimit?: number,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (userId !== undefined && userId !== null) {
      queryParameters = queryParameters.set('userId', userId);
    }
    if (brandId !== undefined && brandId !== null) {
      queryParameters = queryParameters.set('brandId', brandId);
    }
    if (equipmentModelId !== undefined && equipmentModelId !== null) {
      queryParameters = queryParameters.set('equipmentModelId', equipmentModelId);
    }
    if (fromDate !== undefined && fromDate !== null) {
      queryParameters = queryParameters.set('fromDate', fromDate);
    }
    if (toDate !== undefined && toDate !== null) {
      queryParameters = queryParameters.set('toDate', toDate);
    }
    if (topLimit !== undefined && topLimit !== null) {
      queryParameters = queryParameters.set('topLimit', topLimit.toString());
    }

    let headers = this.defaultHeaders;
    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.basePath}/api/admin/my-equipment/repairCostReport`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * GET repaired/purchased parts detail for one piece of equipment (not restricted to its owner)
   * GET /api/admin/my-equipment/{equipmentId}/repairCostDetail
   */
  public apiAdminMyEquipmentRepairCostDetailGet(
    equipmentId: string,
    fromDate?: string,
    toDate?: string,
    observe: 'body' | 'response' | 'events' = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    if (equipmentId === null || equipmentId === undefined) {
      throw new Error(
        'Required parameter equipmentId was null or undefined when calling apiAdminMyEquipmentRepairCostDetailGet.'
      );
    }

    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (fromDate !== undefined && fromDate !== null) {
      queryParameters = queryParameters.set('fromDate', fromDate);
    }
    if (toDate !== undefined && toDate !== null) {
      queryParameters = queryParameters.set('toDate', toDate);
    }

    let headers = this.defaultHeaders;
    let httpHeaderAccepts: string[] = [];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.basePath}/api/admin/my-equipment/${encodeURIComponent(
        String(equipmentId)
      )}/repairCostDetail`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe as 'body',
        reportProgress: reportProgress,
      }
    );
  }
}
